"use client";

import { useId, useState, type ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";

import { MARKETING_EASE } from "@/components/marketing/reveal";

/**
 * A single FAQ accordion row with a smooth, height-animated answer reveal.
 * The question and answer arrive as props from the server FAQ list, so the
 * copy stays in the initial payload; only the open/close interaction is client.
 */
export function FaqRow({
  question,
  answer,
}: {
  question: string;
  answer: ReactNode;
}) {
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <div className="py-5">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full cursor-pointer items-center justify-between gap-4 text-left text-lg font-medium text-white transition-colors hover:text-white/80"
      >
        {question}
        <motion.span
          aria-hidden="true"
          animate={{ rotate: open ? 135 : 0 }}
          transition={{ duration: reduce ? 0 : 0.3, ease: MARKETING_EASE }}
          className="text-xl leading-none text-red-500"
        >
          +
        </motion.span>
      </button>
      <motion.div
        id={panelId}
        initial={false}
        animate={open ? "open" : "closed"}
        variants={{
          open: { height: "auto", opacity: 1 },
          closed: { height: 0, opacity: 0 },
        }}
        transition={{ duration: reduce ? 0 : 0.32, ease: MARKETING_EASE }}
        className="overflow-hidden"
        aria-hidden={!open}
      >
        <div className="pt-3 text-base leading-relaxed text-white/65">{answer}</div>
      </motion.div>
    </div>
  );
}
